import { sha512_256 } from "js-sha512";
import {
  DSContractListing,
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import {
  ChangeEventHandler, useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  ShieldMinus,
  ShieldPlus,
  UserRound,
} from "lucide-react";
import { ApplicationLink, Link } from "@/components/links";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import React from "react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { CopyButton } from "@/components/copy-button";
import {
  cn,
  formatNumber,
  plural,
  shorten,
  showTimeDiffPartial,
} from "@/lib/utils";
import { useInterval } from "usehooks-ts";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import {
  makeProtestTransactions,
  makeUndoProtestTransactions,
} from "@/lib/clients";
import { useWallet } from "@txnlab/use-wallet";
import { Input } from "@/components/ui/input";
import {
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
  Tooltip,
} from "@/components/ui/tooltip";
import { z } from "zod";
import { algod } from "@/lib/algo";
import { ConnectButton } from "@/components/connect-button";
import { clearAlgoAcctInfoCache, useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { clearCStateCache } from "@/hooks/use-cstate";
import { useHistoryEvents } from "@/hooks/use-history-events";

export function UpgradeCard({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  if (!cState || !listing) return null;
  const [open, setOpen] = useState(false);

  const [delta, setDelta] = useState(0);

  const upgradeDelta = (cState: DSContractState) => {
    if (cState.contractUpgrade === null) return;

    const delta =
      cState.contractUpgrade!.applicableTimestamp * 1000 - Date.now();

    setDelta(delta);
  };

  const label = useMemo(() => {
    return showTimeDiffPartial(delta);
  }, [delta]);

  useEffect(() => {
    upgradeDelta(cState);
  }, [cState]);

  useInterval(() => {
    if (cState && delta > 0) upgradeDelta(cState);
  }, 1000);

  const [deployedHash, setDeployedHash] = useState<string>();

  useEffect(() => {
    (async () => {
      if (!cState.contractUpgrade) return;
      const appInfo = await algod
        .getApplicationByID(Number(listing.appId))
        .do();
      const firstPage = Buffer.from(
        appInfo.params["approval-program"],
        "base64"
      ).slice(0, 4096);
      setDeployedHash(sha512_256(firstPage));
    })();
  }, [listing.appId]);

  return (
    <Alert variant="destructive" className="max-w-[640px] w-fit flex">
      <ShieldAlert strokeWidth={2} className="h-6 w-6 -ml-1" />
      <div className="flex flex-col flex-grow">
        <AlertTitle className="flex-grow font-bold mt-1 leading-2">
          {!!cState.contractUpgrade ? (
            <>
              The {listing.lstName} contract is scheduled to{" "}
              <strong>upgrade</strong>.
            </>
          ) : (
            <>
              The {listing.lstName} contract upgrade was{" "}
              <strong>cancelled</strong>.
            </>
          )}
        </AlertTitle>
        <AlertDescription>
          {delta > 0 ? (
            <>
              Applicable in <strong>{label}</strong>.
            </>
          ) : cState.contractUpgrade !== null ? (
            <>Applicable now.</>
          ) : null}{" "}
          {cState.userProtestingStake === 0n ? (
            <>{listing.lstName} holders can protest this.</>
          ) : cState.contractUpgrade === null ? (
            <>
              You can withdraw your protesting stake (
              {formatNumber(cState.userProtestingStake)} {listing.lstName}).
            </>
          ) : (
            <>You have protesting stake.</>
          )}
        </AlertDescription>
      </div>
      <div className="self-center">
        <UpgradeDialog
          open={open}
          setOpen={setOpen}
          cState={cState}
          listing={listing}
          deployedHash={deployedHash}
        >
          <Button variant="destructive">Details</Button>
        </UpgradeDialog>
      </div>
    </Alert>
  );
}

type UpgradeDialogModeType = "" | "index" | "incrprotest" | "decrprotest";
type UpgradeDialogSetMode = React.Dispatch<
  React.SetStateAction<UpgradeDialogModeType>
>;

export function UpgradeDialog({
  open,
  setOpen,
  cState,
  listing,
  children,
  deployedHash,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  cState: DSContractState;
  listing: DSContractListing;
  children: React.ReactNode;
  deployedHash?: string;
}) {
  const [mode, setMode] = useState<UpgradeDialogModeType>("");
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    setMode("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger>{children}</DialogTrigger>
      {mode === "" ? (
        cState.contractUpgrade === null ? (
          <ManageProtestCancelled
            setMode={setMode}
            cState={cState}
            listing={listing}
          />
        ) : (
          <UpgradeDialogIndex
            cState={cState}
            listing={listing}
            setMode={setMode}
            deployedHash={deployedHash}
          />
        )
      ) : mode === "index" ? (
        <ManageProtest setMode={setMode} cState={cState} listing={listing} />
      ) : mode === "incrprotest" ? (
        <IncreaseProtest setMode={setMode} cState={cState} listing={listing} />
      ) : mode === "decrprotest" ? (
        <DecreaseProtest setMode={setMode} cState={cState} listing={listing} />
      ) : null}
    </Dialog>
  );
}

function UpgradeDialogIndex({
  listing,
  cState,
  setMode,
  deployedHash,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setMode: UpgradeDialogSetMode;
  deployedHash?: string;
}) {
  const [delta, setDelta] = useState(0);

  const upgradeDelta = (cState: DSContractState) => {
    if (cState.contractUpgrade === null) return;

    const delta =
      cState.contractUpgrade!.applicableTimestamp * 1000 - Date.now();

    setDelta(delta);
  };
  const label = useMemo(() => {
    return showTimeDiffPartial(delta);
  }, [delta]);

  useEffect(() => {
    upgradeDelta(cState);
  }, [cState]);

  useInterval(() => {
    if (cState && delta > 0) upgradeDelta(cState);
  }, 1000);

  return (
    <DialogContent className="gap-2 flex flex-col">
      <DialogHeader>
        <DialogTitle className="pb-2">Contract Upgrade</DialogTitle>
      </DialogHeader>
      <div>
        The {listing.lstName} contract has a{" "}
        <span className="text-foreground font-bold">code upgrade</span>{" "}
        scheduled.
      </div>
      {delta > 0 ? (
        <div>
          This upgrade is time-locked for another{" "}
          <span className="text-foreground font-bold">{label}</span>.
        </div>
      ) : (
        <div className="text-muted-foreground">
          This upgrade has passed the time-lock.
        </div>
      )}
      <div>
        <UpgradeNotesCollapsible cState={cState} />
      </div>
      <div>
        <UpgradeCodeCollapsible deployedHash={deployedHash} cState={cState} />
      </div>
      <div>
        <UpgradeHashesCollapsible cState={cState} />
      </div>
      {cState.protestCount > 0n ? (
        <div className="p-4 rounded-lg m-2 mx-0 flex justify-between bg-card border flex">
          <div className="flex flex-col">
            <div>Total protesting stake</div>
            <div className="text-sm text-muted-foreground">
              {Number(cState.protestCount)}{" "}
              {plural("account", Number(cState.protestCount))}
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div>
              {formatNumber(cState.protestSum)} {listing.lstName}
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.round(
                (100 * Number(cState.protestSum)) / Number(cState.staked)
              )}
              % of stake
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground">
          No holders are currently protesting this upgrade.
        </div>
      )}
      <div>
        {cState.userProtestingStake === 0n ? (
          <>
            Holders can protest this upgrade.{" "}
            <Link to="https://docs.myth.finance/dualSTAKE/protesting">
              Learn more
            </Link>
            .
          </>
        ) : (
          <>You are protesting this upgrade.</>
        )}
      </div>
      <DialogFooter className="gap-2 md:gap-0 mt-2">
        <DialogClose asChild>
          <Button variant="secondary">Close</Button>
        </DialogClose>
        <ConnectButton onClick={() => setMode("index")}>
          <ShieldPlus />{" "}
          {cState.userProtestingStake === 0n ? "Protest" : "Manage Protest"}
        </ConnectButton>
      </DialogFooter>
    </DialogContent>
  );
}

export function UpgradeNotesCollapsible({
  cState,
}: {
  cState: DSContractState;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [notes, setNotes] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const hash = cState.contractUpgrade?.contractPageHashes[0];
      const url = `https://raw.githubusercontent.com/MythFinance/dualSTAKE/refs/tags/contract-${hash}/metadata/RELEASE_NOTES.txt`;
      try {
        const response = await fetch(url);
        const text = await response.text();
        setNotes(
          text
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
        );
      } catch (e) {
        setNotes(["Error loading release notes:", (e as Error).message]);
      }
    })();
  }, [isOpen]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group">
      <div className="flex items-center justify-between space-x-4">
        <CollapsibleTrigger className="flex items-center gap-0">
          <Button variant="ghost" size="sm">
            <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
            <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
            <span className="sr-only">Toggle</span>
          </Button>
          <h4 className="text-sm text-muted-foreground  font-semibold">
            Upgrade release notes
          </h4>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="space-y-1 pl-4">
        {notes?.length ? (
          notes.map((note, i) => (
            <div
              key={`rls-${i}`}
              className="rounded-md border px-4 py-2 font-mono text-sm shadow-sm"
            >
              {note}
            </div>
          ))
        ) : (
          <div className="rounded-md border px-4 py-2 font-mono text-sm shadow-sm">
            Loading release notes, please wait.
          </div>
        )}
        {/* <div className="rounded-md border px-4 py-2 font-mono text-sm shadow-sm">
          Bug fix: Deleting contract not opted in to paired ASA.
        </div> */}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function UpgradeHashesCollapsible({
  cState,
}: {
  cState: DSContractState;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group">
      <div className="flex items-center justify-between space-x-4">
        <CollapsibleTrigger className="flex items-center gap-0">
          <Button variant="ghost" size="sm">
            <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
            <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
            <span className="sr-only">Toggle</span>
          </Button>
          <h4 className="text-sm text-muted-foreground font-semibold">
            Upgrade code hashes
          </h4>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="space-y-1 pl-4 max-w-full">
        <div className="rounded-md border px-4 py-2 text-sm shadow-sm">
          SHA512/256 hashes of upgrade code pages
        </div>
        {cState.contractUpgrade?.contractPageHashes.map((c) => (
          <div className="rounded-md border px-4 py-2 font-mono text-sm shadow-sm flex items-center gap-2">
            <CopyButton value={c} />
            <div className="break-all">{c}</div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function UpgradeCodeCollapsible({
  cState,
  deployedHash,
}: {
  deployedHash?: string;
  cState: DSContractState;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group">
      <div className="flex items-center justify-between space-x-4">
        <CollapsibleTrigger className="flex items-center gap-0">
          <Button variant="ghost" size="sm">
            <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
            <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
            <span className="sr-only">Toggle</span>
          </Button>
          <h4 className="text-sm text-muted-foreground font-semibold">
            Upgrade code source
          </h4>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="space-y-1 pl-4 max-w-full">
        <div className="rounded-md border px-4 py-2 text-sm shadow-sm">
          The upgrade code can be seen{" "}
          <Link
            to={`https://github.com/MythFinance/dualSTAKE/tree/contract-${cState.contractUpgrade?.contractPageHashes[0]}`}
          >
            here
          </Link>
          .
        </div>
        <div className="rounded-md border px-4 py-2 text-sm shadow-sm">
          {deployedHash ? (
            <>
              View{" "}
              <Link
                to={`https://github.com/MythFinance/dualSTAKE/compare/contract-${deployedHash}..contract-${cState.contractUpgrade?.contractPageHashes[0]}`}
              >
                code differences
              </Link>{" "}
              from deployed version.
            </>
          ) : (
            <>Loading code diff, please wait.</>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ManageProtestCancelled({
  listing,
  cState,
  setMode,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setMode: UpgradeDialogSetMode;
}) {
  return (
    <DialogContent className="gap-2 flex flex-col">
      <DialogHeader>
        <DialogTitle className="pb-2">Upgrade Cancelled</DialogTitle>
      </DialogHeader>
      <div className="text-foreground">
        The {listing.lstName} contract upgrade was cancelled.
      </div>
      <div className="text-muted-foreground">
        You can withdraw your protesting stake.
      </div>
      <YourProtestingStake cState={cState} />
      <DialogFooter className="gap-2 md:gap-0 mt-2">
        <Button onClick={() => setMode("")} variant="secondary">
          Go back
        </Button>
        <Button
          disabled={cState.userProtestingStake === 0n}
          onClick={() => setMode("decrprotest")}
        >
          <ShieldMinus /> Withdraw Protest
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ManageProtest({
  listing,
  cState,
  setMode,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setMode: UpgradeDialogSetMode;
}) {
  return (
    <DialogContent className="gap-2 flex flex-col">
      <DialogHeader>
        <DialogTitle className="pb-2">Upgrade Protest</DialogTitle>
      </DialogHeader>
      <div className="text-foreground">
        The contract upgrade can be{" "}
        <span className="font-bold">soft-blocked</span> by locking up{" "}
        {listing.lstName} in protest.
      </div>
      <div className="text-muted-foreground">
        Before the upgrade to take place, protesting stake must be dissolved
        into ALGO+{listing.asaUnitName} and returned to protesting users.{" "}
        <Link to="https://docs.myth.finance/dualSTAKE/protesting">
          Learn more
        </Link>
        .
      </div>
      <YourProtestingStake cState={cState} />
      <DialogFooter className="gap-2 md:gap-0 mt-2">
        <Button onClick={() => setMode("")} variant="secondary">
          Go back
        </Button>
        <Button
          disabled={cState.userProtestingStake === 0n}
          onClick={() => setMode("decrprotest")}
        >
          <ShieldMinus /> Withdraw Protest
        </Button>
        <Button onClick={() => setMode("incrprotest")}>
          <ShieldPlus /> Increase Protest
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function YourProtestingStake({ cState }: { cState: DSContractState }) {
  return (
    <div className="p-4 rounded-lg m-2 flex justify-between bg-card border">
      <div>Your protesting stake</div>
      <div>
        {formatNumber(cState.userProtestingStake)} {cState.lstName}
      </div>
    </div>
  );
}

function IncreaseProtest({
  listing,
  cState,
  setMode,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setMode: UpgradeDialogSetMode;
}) {
  const { refetch: refetchHistoryEvents } = useHistoryEvents(cState.appId);
  const { activeAddress } = useWallet();
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const { lstId, lstName } = cState;
  const { data: accountInfo, refetch: refetchAlgoAcct } =
    useAlgoAcctInfo(activeAddress);
  const userLstBalance = useBalance(lstId, accountInfo);

  const amountSchema = z
    .string()
    .refine(
      (val) => {
        if (!val) return true;
        const num = Number(val);
        return !isNaN(num) && num >= 0;
      },
      { message: "Please enter a valid positive amount" }
    )
    .refine(
      (val) => {
        if (!val) return true;
        const num = Number(val);
        const baseNum = num * 10 ** 6;
        return !isNaN(num) && baseNum >= 1_000_000;
      },
      { message: `Minimum protest amount: 1 ${lstName}` }
    )
    .refine(
      (val) => {
        if (!val) return true;
        const num = Number(val);
        const baseNum = num * 10 ** 6;
        const balance = userLstBalance;
        return !isNaN(num) && balance >= baseNum;
      },
      { message: `Not enough ${lstName}` }
    );

  useEffect(() => {
    refetchAlgoAcct();
  }, [activeAddress, lstId]);

  const onSuccess = () => {
    clearCStateCache(cState.appId);
    refetchHistoryEvents();
    clearAlgoAcctInfoCache();
    setMode(cState.contractUpgrade === null ? "" : "index");
  };

  const changeAmount: ChangeEventHandler<HTMLInputElement> = (amt) => {
    setAmount(amt.currentTarget.value);
  };

  useEffect(() => {
    const amountValidation = amountSchema.safeParse(amount);
    if (!amountValidation.success) {
      setAmountError(amountValidation.error.errors[0].message);
      return;
    }
    setAmountError(undefined);
  }, [amount]);

  const setMax = (e: any) => {
    e.preventDefault();
    const value = userLstBalance;
    const valueStr = String(value / 1e6);
    const arg = {
      currentTarget: {
        value: valueStr,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    changeAmount(arg);
    inputRef.current!.value = valueStr;
    return false;
  };

  return (
    <DialogContent className="gap-2 flex flex-col">
      <DialogHeader>
        <DialogTitle className="pb-2">Protest Upgrade</DialogTitle>
      </DialogHeader>
      <YourProtestingStake cState={cState} />

      {userLstBalance === 0 ? (
        <div className="text-destructive mb-1">
          You do not have any {cState.userProtestingStake > 0n ? "more" : ""}{" "}
          {lstName} to protest with.
        </div>
      ) : (
        <div className="p-4 pt-3 rounded-lg mt-0 mx-2 mb-1 flex-col gap-4  flex justify-between bg-card border">
          <div className="flex items-center gap-2">
            Add protesting stake
            <Button
              onClick={setMax}
              className={cn(
                "px-3 py-1 text-xs rounded-md transition-colors h-auto",
                "bg-muted/30 hover:bg-muted/50 text-muted-foreground/90",
                "focus:outline-none focus:ring-1 focus:ring-ring"
              )}
              size="icon"
              variant="outline"
            >
              MAX
            </Button>
          </div>

          <div className="flex items-center gap-3 max-w-96 self-center">
            <TooltipProvider>
              <Tooltip open={!!amountError}>
                <TooltipTrigger asChild>
                  <Input
                    value={amount ?? ""}
                    onChange={changeAmount}
                    className={amountError ? "border-destructive" : ""}
                    ref={inputRef}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className={`bg-destructive`}>
                  {amountError}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div>{lstName}</div>
          </div>
        </div>
      )}

      <DialogFooter className="gap-2 md:gap-0 mt-2">
        <Button onClick={() => setMode("index")} variant="secondary">
          Go back
        </Button>
        <TransactionDialog
          type="protest"
          trigger={(onClick) => (
            <Button
              onClick={onClick}
              disabled={!!amountError || !amount || Number(amount) === 0}
            >
              <ShieldPlus />
              Increase Protest
            </Button>
          )}
          onSuccess={onSuccess}
          transactionBuilder={() =>
            makeProtestTransactions(
              listing.appId,
              activeAddress!,
              Math.floor(Number(amount) * 1e6)
            )
          }
          title={`Protesting ${lstName} contract upgrade`}
        >
          {transactionInner(
            "protest",
            <div className="flex flex-col gap-2">
              <div>
                You are going to{" "}
                <strong className="font-bold">lock up {lstName}</strong> to
                protest upgrading the contract.
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" /> Locking up
                </div>{" "}
                <span className="text-right text-muted-foreground">
                  {amount} {listing.lstName}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </DialogFooter>
    </DialogContent>
  );
}

function DecreaseProtest({
  listing,
  cState,
  setMode,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setMode: UpgradeDialogSetMode;
}) {
  const { refetch: refetchHistoryEvents } = useHistoryEvents(cState.appId);
  const { activeAddress } = useWallet();

  return (
    <DialogContent className="gap-2 flex flex-col">
      <DialogHeader>
        <DialogTitle className="pb-2">Withdraw Protesting stake</DialogTitle>
      </DialogHeader>
      <YourProtestingStake cState={cState} />
      <DialogFooter className="gap-2 md:gap-0 mt-2">
        <Button
          onClick={() =>
            setMode(cState.contractUpgrade === null ? "" : "index")
          }
          variant="secondary"
        >
          Go back
        </Button>
        <TransactionDialog
          type="withdrawal"
          trigger={(onClick) => (
            <Button onClick={onClick}>
              <ShieldMinus />
              Withdraw Protest
            </Button>
          )}
          onSuccess={() => {
            clearCStateCache(cState.appId);
            refetchHistoryEvents();
            setMode(cState.contractUpgrade === null ? "" : "index");
          }}
          transactionBuilder={() =>
            makeUndoProtestTransactions(listing.appId, activeAddress!)
          }
          title={`Withdraw protesting stake`}
        >
          {transactionInner(
            "withdrawal",
            <div className="flex flex-col gap-2">
              <div>
                You are going to <strong className="font-bold">withdraw</strong>{" "}
                your protest stake from the{" "}
                <span className="font-bold">{listing.lstName}</span> contract.{" "}
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center ml-3">
                  <ChevronLeft size={18} className="mr-1" /> Receiving
                </div>{" "}
                <span className="text-right">
                  {formatNumber(cState.userProtestingStake)} {listing.lstName}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </DialogFooter>
    </DialogContent>
  );
}
